import { Injectable } from '@angular/core';

import { HttpService } from './http.service';
import { loadScript } from "@paypal/paypal-js";
import { Output, EventEmitter } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

declare var Stripe: any;

export interface WorldpayCheckout {
    generateSessionState(callback: (error: { message?: string } | null, session?: string) => void): void;
    remove(): void;
}

export interface WorldpayCheckoutConfig {
    id: string;
    form: string;
    fields: {
        pan: { selector: string, placeholder: string };
        expiry: { selector: string, placeholder: string };
        cvv: { selector: string, placeholder: string };
    };
    styles: { [selector: string]: { [property: string]: string } };
}

export interface WorldpaySdk {
    checkout: {
        init(config: WorldpayCheckoutConfig,
            callback: (error: { message?: string } | null, checkout?: WorldpayCheckout) => void): void;
    };
}

declare global {
    interface Window {
        Worldpay?: WorldpaySdk;
    }
}

@Injectable({
    providedIn: 'root'
})
export class PaymentService {
    public paypalLoaded = new BehaviorSubject(false);
    public stripeLoaded: EventEmitter<string> = new EventEmitter();

    public stripeKey: string;
    public paypalKey: string;
    public paypalEnv: string;
    public worldpayKey: string;
    private worldpayState = new BehaviorSubject<WorldpaySdk | Error | null>(null);
    public stripe: any;
    public paypal: any;
    public elements: any;
    public secure = false;
    public initialized = false;
    public fees: { min: number, fee: number }[] = [];
    public feeLabel = '';
    private feeType = 'tiers';
    private feePercent = 0;
    private feeBase = 0;
    public profiles: string[] = [];
    public currency = 'GBP';

    constructor(
        private http: HttpService
    ) { }

    public getBookingFee(total: number): number {
        if (total <= 0) return 0;
        if (this.feeType === 'percent') {
            return Math.round((total * this.feePercent / 100 + this.feeBase) * 100) / 100;
        }
        return this.fees.reduce((fee, tier) => tier.min < total ? tier.fee : fee, 0);
    }

    public init(p: string, res: any, options?: any): void {
        if (this.initialized) return;
        this.initialized = true;

        const version = this.http.mode;
        const threshold = { ...res.booking_fee.threshold };
        this.feeType = res.booking_fee.type || 'tiers';
        this.feePercent = +(res.booking_fee.percent ?? 0);
        this.feeBase = +(res.booking_fee.base ?? 0);
        this.feeLabel = res.booking_fee.label || '';

        const type = p || 'default';
        const profiles = (res[type] || res.default)[version];

        this.fees = Object.keys(threshold).map(min => ({ min: +min, fee: +threshold[min] }));
        this.profiles = profiles.map(item => item.processor);
        this.paypalEnv = this.http.isTestMode ? 'sandbox' : 'production';

        if(this.http.isTestMode) console.log('payments in test mode');

        if (this.profiles.includes('stripe')) {
            const stripe = profiles.find(item => item.processor === 'stripe');
            this.stripeKey = stripe.key;
            this.secure = stripe['3d_secure'];

            this.initStripe();
        }

        if (this.profiles.includes('worldpay') && !this.profiles.includes('stripe')) {
            const worldpay = profiles.find(item => item.processor === 'worldpay');
            this.worldpayKey = worldpay.key;
            this.initWorldpay();
        }

        if (this.profiles.includes('paypal')) {
            /*if(Object.values(document.getElementsByTagName('iframe')).filter(it=>it.title == 'PayPal').length == 0
                && !document.getElementById('paypalScriptLoaded')){
                let el = document.createElement('span') as any;
                el.id='paypalScriptLoaded';
                el.title=""+Math.random()*1000000;

                document.getElementsByTagName('body')[0].appendChild(el);
            */
                
                //Object.values(document.getElementsByTagName('iframe')).filter(it=>it.title === 'PayPal').length;
                const paypal = profiles.find(item => item.processor === 'paypal');

                //let extra_options:{[key:string]:string} = {"disable-funding":'card,bancontact,credit'};
                let extra_options:{[key:string]:string} = {"disable-funding":'card,bancontact'};
                //below is disabled for now
                if(options?.paylater){ 
                    //extra_options = {"disable-funding":'card,bancontact','enable-funding':'paylater'}
                    extra_options = {"disable-funding":'card,bancontact','enable-funding':'paylater'}
                    //extra_options = {'enable-funding':'paylater'}
                }
            
                //if(Object.values(document.getElementsByTagName('iframe')).filter(it=>it.title == 'PayPal').length != 0 || document.getElementById('paypalScriptLoaded').title!==el.title) return; //already there
                this.paypalKey = paypal.key;
                /*this.loadScript(
                    `https://www.paypal.com/sdk/js?client-id=${this.paypalKey}&disable-funding=card,credit,bancontact&currency=${this.currency}&intent=authorize`,
                    ()=>{
                        console.log('paypal loaded')},
                    (err)=>{console.log('error'+JSON.stringify(err))}
                );*/
                
                    loadScript({
                     ...extra_options   ,
                     "clientId":this.paypalKey,
                    "components":"messages,buttons",
                    "currency":this.currency,
                    "intent":"authorize"})
                        .then((paypal)=>{
                            this.paypalLoaded.next(true);
                            this.paypal = paypal
                        })
                        .catch((err)=>{console.log('error-paypal loadScript'+JSON.stringify(err))})
                    ;
        }

        if(!this.profiles.some(profile => ['stripe', 'paypal', 'worldpay'].includes(profile))) console.log('no payment method found');
    }

    private initWorldpay(): void {
        if (!this.worldpayKey) {
            this.worldpayState.next(new Error('Worldpay checkout ID is missing from the payment profile'));
            return;
        }

        const url = this.http.isTestMode
            ? 'https://try.access.worldpay.com/access-checkout/v2/checkout.js'
            : 'https://access.worldpay.com/access-checkout/v2/checkout.js';
        const loaded = () => {
            this.worldpayState.next(window.Worldpay?.checkout
                ? window.Worldpay
                : new Error('Worldpay checkout SDK is unavailable'));
        };
        const failed = () => this.worldpayState.next(new Error('Unable to load Worldpay checkout'));
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
        if (existing) {
            if (window.Worldpay?.checkout) loaded();
            else {
                existing.addEventListener('load', loaded, { once: true });
                existing.addEventListener('error', failed, { once: true });
            }
            return;
        }

        const script = document.createElement('script');
        script.src = url;
        // Worldpay requires at least the origin for its hosted-field CORS checks.
        script.referrerPolicy = 'origin';
        script.onload = loaded;
        script.onerror = failed;
        document.head.appendChild(script);
    }

    public createWorldpayCheckout(formId: string): Observable<WorldpayCheckout> {
        return this.worldpayState.pipe(
            filter(state => state !== null),
            take(1),
            switchMap(state => {
                if (state instanceof Error) return throwError(() => state);
                return new Observable<WorldpayCheckout>(subscriber => {
                    let removed = false;
                    let instance: WorldpayCheckout;
                    state.checkout.init({
                        id: this.worldpayKey,
                        form: `#${formId}`,
                        fields: {
                            pan: { selector: `#${formId}-pan`, placeholder: 'Card number' },
                            expiry: { selector: `#${formId}-expiry`, placeholder: 'MM/YY' },
                            cvv: { selector: `#${formId}-cvv`, placeholder: 'CVC' }
                        },
                        styles: {
                            input: {
                                'font-size': '16px',
                                'font-family': '"Helvetica Neue", Helvetica, sans-serif',
                                color: '#374151'
                            },
                            'input.is-invalid': { color: '#fa755a' }
                        }
                    }, (error, checkout) => {
                        instance = checkout;
                        if (removed) {
                            checkout?.remove();
                            return;
                        }
                        if (error || !checkout) {
                            subscriber.error(new Error(error?.message || 'Unable to initialize Worldpay checkout'));
                            return;
                        }
                        subscriber.next(checkout);
                    });
                    // Keep ownership until the component unsubscribes, including late SDK callbacks.
                    return () => {
                        removed = true;
                        instance?.remove();
                    };
                });
            })
        );
    }

    public getWorldpayToken(checkout: WorldpayCheckout): Observable<{ id: string }> {
        return new Observable(subscriber => {
            checkout.generateSessionState((error, session) => {
                if (error || !session) {
                    subscriber.error({
                        type: 'card_error',
                        message: error?.message || 'Unable to tokenize Worldpay card details'
                    });
                    return;
                }
                subscriber.next({ id: session });
                subscriber.complete();
            });
        });
    }

    private initStripe(){
        if(typeof(Stripe) === "undefined"){
            this.loadScript(`https://js.stripe.com/v3/`,function(){
                this.stripe = Stripe(this.stripeKey);
                this.elements = this.stripe.elements();
                this.stripeLoaded.emit('loaded');
            }.bind(this));
        }else{
            this.stripe = Stripe(this.stripeKey);
            this.elements = this.stripe.elements();
            this.stripeLoaded.emit('loaded');
        }
    }

    private loadScript(url: string, onload: Function|null=null, onerror: Function|null=null) {
        if (document.querySelector(`script[src="${url}"]`)) return;


        const script = document.createElement('script') as any;

        script.type = 'text/javascript';
        script.src = url;
        script.referrerpolicy = "same-origin"
        script.crossorigin='crossorigin';

        if(onload){
            script.onload = onload;
        }
        if(onerror){
            script.onerror = onerror;
        }

        document.getElementsByTagName('head')[0].appendChild(script);
    }
}
